import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

export default function CookieConsent() {
    const [isVisible, setIsVisible] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
        const consent = localStorage.getItem("site_consent");
        if (!consent) {
            setTimeout(() => setIsVisible(true), 1500);
        }
    }, []);

    const handleAccept = () => {
        localStorage.setItem("site_consent", "true");
        setIsVisible(false);
    };

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 100, opacity: 0 }}
                    transition={{ duration: 0.5, ease: "circOut" }}
                    className="fixed bottom-6 left-6 z-[90] w-[calc(100%-3rem)] max-w-[400px]"
                >
                    {/* Main Container - Thick Border, Dark Theme, Crystallize Effect */}
                    <div className="bg-[#121212] border-2 border-[#333] rounded-3xl p-6 relative overflow-hidden group">

                        {/* Crystallize / Noise Texture (From Navbar) */}
                        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.08] pointer-events-none" />

                        {/* Decorative background glow (Updated to Accent) */}
                        <div className="absolute top-0 right-0 w-32 h-32 bg-[var(--accent)]/5 rounded-full blur-3xl -z-10 transition-colors duration-500 group-hover:bg-[var(--accent)]/10" />

                        <div className="flex flex-col relative z-10">
                            {/* Header Section: Icon + Title + Info Toggle */}
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-2xl bg-[#111] border-2 border-[#333] flex items-center justify-center text-[var(--accent)] shrink-0">
                                        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5" />
                                            <path d="M8.5 8.5v.01" />
                                            <path d="M16 15.5v.01" />
                                            <path d="M12 12v.01" />
                                            <path d="M11 17v.01" />
                                            <path d="M7 14v.01" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h4 className="text-white font-black text-lg tracking-tight uppercase">Çerezler</h4>
                                        <p className="text-[#666] text-xs font-bold tracking-widest uppercase">Veri Politikası</p>
                                    </div>
                                </div>

                                {/* Info Toggle Icon (Top Right) */}
                                <button
                                    onClick={() => setIsExpanded(!isExpanded)}
                                    className={`w-10 h-10 flex items-center justify-center rounded-xl border-2 transition-all duration-300 ${isExpanded ? 'border-[var(--accent)] text-[var(--accent)] bg-[var(--accent)]/10 shadow-[0_0_15px_rgba(var(--accent-rgb),0.2)]' : 'border-[#333] text-[#666] hover:text-white hover:border-[#666] hover:bg-[#222]'}`}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
                                </button>
                            </div>

                            {/* Brief Text */}
                            <p className="text-[#888] text-sm font-medium leading-relaxed mb-4">
                                Sitemizi geliştirmek ve ziyaretçi trafiğini analiz etmek için çerez teknolojisinden faydalanıyoruz.
                            </p>

                            {/* Expanded Details Section */}
                            <AnimatePresence>
                                {isExpanded && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0, marginBottom: 0 }}
                                        animate={{ height: "auto", opacity: 1, marginBottom: 16 }}
                                        exit={{ height: 0, opacity: 0, marginBottom: 0 }}
                                        transition={{ duration: 0.4, ease: "easeInOut" }}
                                        className="overflow-hidden"
                                    >
                                        <div className="py-4 border-t-2 border-[#222]">
                                            <h5 className="text-white font-bold text-xs uppercase tracking-wider mb-3">Toplanan Veriler</h5>
                                            <ul className="grid grid-cols-2 gap-y-2 gap-x-4 text-[11px] text-[#888] font-medium mb-4">
                                                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-[var(--accent)] rounded-full shrink-0" />IP Adresi</li>
                                                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-[var(--accent)] rounded-full shrink-0" />Konum (Ülke/Şehir)</li>
                                                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-[var(--accent)] rounded-full shrink-0" />Cihaz & İşletim Sistemi</li>
                                                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-[var(--accent)] rounded-full shrink-0" />Tarayıcı Bilgisi</li>
                                                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-[var(--accent)] rounded-full shrink-0" />Sayfa Görüntülemeleri</li>
                                                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-[var(--accent)] rounded-full shrink-0" />Ziyaret Zamanı</li>
                                            </ul>
                                            <p className="text-[#666] text-[10px] leading-relaxed">
                                                Bu verileri; ziyaretçi kitlemi analiz etmek, hangi ülkelerden ilgi olduğunu anlamak, pazar belirlemek ve buna göre ürün geliştirmek amacıyla topluyorum.
                                            </p>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Actions Row */}
                            <div className="flex items-center gap-3">
                                {/* Accept Button (Expanded to Full Width, "ANLADIM") */}
                                <button
                                    onClick={handleAccept}
                                    className="flex-1 w-full bg-[var(--accent)]/10 hover:bg-[var(--accent)]/20 border-2 border-[var(--accent)] text-[var(--accent)] font-bold py-3 rounded-xl transition-all duration-300 active:scale-95 text-xs uppercase tracking-widest flex items-center justify-center group/btn shadow-[0_0_10px_rgba(var(--accent-rgb),0.1)] hover:shadow-[0_0_20px_rgba(var(--accent-rgb),0.25)]"
                                >
                                    ANLADIM
                                </button>
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
